import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { AppState } from 'app/ngrx/app-state/app-state';
import { receiveUpdate } from 'app/ngrx/live-updates/live-updates.actions';
import { Observable, ReplaySubject, timer } from 'rxjs';
import { share } from 'rxjs/operators';
import { pickTruthy } from 'Util';
import { LiveUpdateService } from './live-update.service';

@Injectable({
	providedIn: 'root',
})
export class LiveStoreService {
	/**
	 * If no subscribers are present, we wait this long before disconnecting to
	 * avoid unnecessary refetches. This should be long enough to cover most
	 * cases where components are unmounted and remounted quickly, but not too
	 * long as to consume resources unnecessarily.
	 **/
	private DISCONNECTION_DELAY = 1000;

	constructor(private liveUpdate: LiveUpdateService, private store: Store<AppState>) {
		this.liveUpdate.listen().subscribe((event) => this.store.dispatch(receiveUpdate({ event })));
	}

	/**
	 * Helper for creating live-updating Observables of data in the store. Setting up a live
	 * data collection involves a few steps:
	 * 1. Create this Observable (usually in a standalone Service), and pass in the action
	 * 	  to dispatch to fetch the initial data.
	 * 2. In NgRx, create an effect that listens for the `receiveUpdate` action, filters to
	 *    relevant actions, and dispatches the parsed/validated action to update the store.
	 * 3. In NgRx, write a reducer to handle the live update and initial fetch actions.
	 * 4. In your component, subscribe to this Observable and pipe it to a relevant selector
	 *    to get the data you need.
	 *
	 * (Why not just use an effect to listen for WebSocket reconnects directly?) This Observable
	 * tracks references. If the WebSocket connection is dropped and reconnects, it will only
	 * re-fetch the initial data *if* there are any subscribers. If we managed this entirely in NgRx,
	 * we would need to track the references manually. This also allows us to delay the reference
	 * counting to avoid unnecessary refetches when components are unmounted and remounted quickly.
	 *
	 * @param onConnectAction An action to dispatch when the connection is (re-)established.
	 * This action should populate the initial state of the store, i.e. fetch initial API data.
	 * @param isConnected$ Observable that emits true when the connection is established.
	 * @returns An observable that emits the full store state. Typically, you will pipe this to
	 * a relevant selector to get the data you need.
	 */
	createLiveStoreObservable(onConnectAction: () => Action): Observable<AppState> {
		return new Observable<AppState>((subscriber) => {
			const reconnectSubscription = this.liveUpdate.isConnected$.pipe(pickTruthy()).subscribe(() => {
				this.store.dispatch(onConnectAction());
			});

			const storeSubscription = this.store.subscribe(subscriber);
			return () => {
				reconnectSubscription.unsubscribe();
				storeSubscription.unsubscribe();
			};
		}).pipe(
			// Ensure that the observable only initializes once
			// and shares the results with all subscribers:
			share({
				connector: () => new ReplaySubject(1),
				// We delay disconnects to ensure that we don't have to refetch initial
				// state as components are unmounted and remounted.
				resetOnRefCountZero: () => timer(this.DISCONNECTION_DELAY),
			})
		);
	}
}
